import { useState } from "react";
import { useNavigate } from "react-router";
import {
  Bot, MessageSquare, Pause, Pencil, Play, Sparkles,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PageSpinner } from "@/components/ui/page-spinner";
import { supabase } from "../providers/supabase/supabase";
import { AtivarAgenteDialog } from "./AtivarAgenteDialog";
import { GuiaDoAgente } from "./GuiaDoAgente";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useAgentHub } from "./useAgentHub";
import { TextoMarkdown } from "../misc/TextoMarkdown";
import { CabecalhoPagina } from "../bento/ui";
import type { AgentInstance, AgentTemplate } from "./useAgentHub";

/**
 * "Meu Agente": galeria de templates + os agentes já ativados pelo usuário.
 *
 * Fluxo: card do template -> wizard (identidade, capacidades, rotina) -> a
 * instância nasce em `draft` -> Ativar.
 *
 * O briefing é entregue AQUI (agent_runs.detail.text, exibido no card). A
 * conversa NÃO: desde a fusão com a North IA (24/09/2026) existe um chat só, em
 * /ai, com a carteira, a estruturação de negócio e seguros juntos — e com o
 * que se configura aqui (instruções pessoais, capacidades ligadas). "Conversar"
 * leva pra lá. Antes era um chat próprio por agente, e duas IAs no mesmo
 * produto davam duas respostas diferentes para a mesma pergunta.
 *
 * O vínculo com Telegram saiu da tela — o canal deixou de ser usado. O servidor
 * ainda entrega briefing em quem já estava vinculado (agent_instances
 * .channel_chat_id); o que não existe mais é criar vínculo novo.
 */

const formatarData = (iso: string) =>
  new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })
    .format(new Date(iso));

type EstiloStatus = { label: string; variant: "default" | "secondary" | "outline" };

const STATUS_BADGE: Record<string, EstiloStatus> = {
  draft: { label: "Rascunho", variant: "outline" },
  active: { label: "Ativo", variant: "default" },
  paused: { label: "Pausado", variant: "secondary" },
};

/**
 * Status que o banco devolve e a tela não conhece não pode derrubar a página:
 * mostra o valor cru e segue. Acontece quando alguém acrescenta um status em
 * `agent_instances` antes de o front saber dele.
 */
const estiloDoStatus = (status: string | null | undefined): EstiloStatus =>
  STATUS_BADGE[status ?? ""] ?? { label: status ?? "—", variant: "outline" };

export const MeuAgentePage = () => {
  const {
    loading, error, reload, currentCrmUserId,
    templates, capabilities, instanceByTemplate, connectedProviders, lastRunByInstance,
  } = useAgentHub();

  const [dialog, setDialog] = useState<{ template: AgentTemplate; instance?: AgentInstance } | null>(null);
  const [briefingAberto, setBriefingAberto] = useState<{ texto: string; quando: string } | null>(null);

  const navigate = useNavigate();
  const [busyId, setBusyId] = useState<number | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const setStatus = async (instance: AgentInstance, status: AgentInstance["status"]) => {
    setBusyId(instance.id);
    setActionError(null);
    try {
      const { error: updateError } = await supabase
        .from("agent_instances")
        .update({ status })
        .eq("id", instance.id);
      if (updateError) throw updateError;
      await reload();
    } catch (err) {
      console.error("Erro ao mudar status do agente:", err);
      setActionError("Não foi possível mudar o status do agente.");
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <PageSpinner />;

  return (
    <div className="space-y-6">
      {/* Premium North: o cabeçalho de página com o degradê marinho. */}
      <CabecalhoPagina
        titulo={
          <span className="flex items-center gap-2">
            <Bot className="h-6 w-6 text-ouro" />
            Meu Agente North
          </span>
        }
        descricao="Assistente com acesso à carteira, alertas de contato e respostas na plataforma."
      />

      {(error || actionError) && (
        <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error || actionError}
        </p>
      )}


      {/* Fechado por padrão: quase tudo aqui só é descoberto por quem já sabe
          que existe — o envio de e-mail em especial, que funciona por pedido em
          linguagem natural e não tem botão. */}
      <GuiaDoAgente />

      {templates.length === 0 && !error && (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Nenhum modelo de agente disponível.
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {templates.map((template) => {
          const instance = instanceByTemplate.get(template.slug);
          const busy = instance != null && busyId === instance.id;
          const ultimoBriefing = instance ? lastRunByInstance.get(instance.id) : undefined;

          return (
            <Card key={template.slug} className="flex flex-col">
              <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
                <CardTitle className="flex items-center gap-2 text-base">
                  <span className="text-xl" aria-hidden>{template.emoji}</span>
                  {instance?.display_name || template.name}
                </CardTitle>
                {instance && (
                  <Badge variant={estiloDoStatus(instance.status).variant}>
                    {estiloDoStatus(instance.status).label}
                  </Badge>
                )}
              </CardHeader>

              <CardContent className="flex flex-1 flex-col gap-4">
                <p className="text-sm text-muted-foreground">{template.description}</p>

                {!instance ? (
                  <Button className="mt-auto w-full" onClick={() => setDialog({ template })}>
                    <Sparkles className="h-4 w-4 mr-1" /> Ativar
                  </Button>
                ) : (
                  <div className="mt-auto space-y-3">
                    <p className="text-xs text-muted-foreground">
                      {/* capacidade nunca configurada vem null do banco, não [] */}
                      {(instance.enabled_capabilities ?? []).length} capacidade
                      {(instance.enabled_capabilities ?? []).length === 1 ? "" : "s"} ligada
                      {(instance.enabled_capabilities ?? []).length === 1 ? "" : "s"}
                    </p>

                    {/* Último briefing — a entrega padrão é aqui no CRM. */}
                    {ultimoBriefing?.detail?.text && (
                      <button
                        type="button"
                        onClick={() =>
                          setBriefingAberto({
                            texto: ultimoBriefing.detail!.text!,
                            quando: formatarData(ultimoBriefing.started_at),
                          })
                        }
                        className="w-full rounded-lg border bg-muted/40 p-3 space-y-1.5 text-left transition-colors hover:bg-muted/70"
                      >
                        <p className="text-xs font-medium flex items-center gap-1.5">
                          <Sparkles className="h-3.5 w-3.5 text-primary" />
                          Último briefing · {formatarData(ultimoBriefing.started_at)}
                        </p>
                        <TextoMarkdown
                          texto={ultimoBriefing.detail.text}
                          className="max-h-40 space-y-1.5 overflow-hidden text-sm leading-relaxed text-muted-foreground"
                        />
                        <p className="text-xs text-primary">Ver completo</p>
                      </button>
                    )}

                    <div className="flex flex-wrap gap-2">
                      {/* Conversar vem primeiro: é o que se faz todo dia,
                          editar é o que se faz uma vez. A conversa é na North
                          IA — o chat é um só desde a fusão. */}
                      <Button size="sm" onClick={() => navigate("/ai")}>
                        <MessageSquare className="h-3.5 w-3.5 mr-1" /> Conversar
                      </Button>

                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDialog({ template, instance })}
                      >
                        <Pencil className="h-3.5 w-3.5 mr-1" /> Editar
                      </Button>

                      {instance.status === "active" ? (
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={busy}
                          onClick={() => setStatus(instance, "paused")}
                        >
                          <Pause className="h-3.5 w-3.5 mr-1" /> Pausar
                        </Button>
                      ) : (
                        <Button size="sm" disabled={busy} onClick={() => setStatus(instance, "active")}>
                          <Play className="h-3.5 w-3.5 mr-1" /> Ativar
                        </Button>
                      )}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Dialog open={!!briefingAberto} onOpenChange={(o) => !o && setBriefingAberto(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <Sparkles className="h-4 w-4 text-primary" />
              Briefing · {briefingAberto?.quando}
            </DialogTitle>
          </DialogHeader>
          <TextoMarkdown
            texto={briefingAberto?.texto ?? ""}
            className="max-h-[65vh] space-y-2 overflow-y-auto text-sm leading-relaxed"
          />
        </DialogContent>
      </Dialog>

      {dialog && currentCrmUserId != null && (
        <AtivarAgenteDialog
          template={dialog.template}
          instance={dialog.instance}
          capabilities={capabilities}
          connectedProviders={connectedProviders}
          crmUserId={currentCrmUserId}
          open
          onOpenChange={(open) => !open && setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await reload();
          }}
        />
      )}
    </div>
  );
};
